/**
 * Onboarding wizard step bodies. Each step is a pure presentation component:
 *   - Receives `form` + `upd` from the parent wizard.
 *   - No local state, no side effects — keeps wizard cyclomatic complexity low.
 */
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { KeyRound, Award, ShieldCheck } from "lucide-react";
import DatePickerField from "@/components/DatePickerField";

const ROLES = ["Pharmacist", "Technician", "Intern", "Administrative"];
const DEPARTMENTS = ["Dispensing", "Sterile Lab", "Non-Sterile Lab", "Administrative", "Delivery"];

const Field = ({ k, label, type = "text", full, mono, options, value, onChange, minDate }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs text-slate-600 mb-1 block">{label}</Label>
    {options ? (
      <select data-testid={`onboard-${k}`} value={value} onChange={(e) => onChange(k, e.target.value)}
        className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : type === "date" ? (
      <DatePickerField testid={`onboard-${k}`} value={value ?? ""} onChange={(v) => onChange(k, v)} minDate={minDate} />
    ) : (
      <Input data-testid={`onboard-${k}`} type={type} value={value ?? ""}
        onChange={(e) => onChange(k, e.target.value)} className={mono ? "font-mono" : ""} />
    )}
  </div>
);

export function StepBasics({ form, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field k="name" label="Full Name *" full value={form.name} onChange={upd} />
      <Field k="title" label="Title / Position" value={form.title} onChange={upd} />
      <Field k="employee_role" label="Role *" options={ROLES} value={form.employee_role} onChange={upd} />
      <Field k="department" label="Department" options={DEPARTMENTS} value={form.department} onChange={upd} />
      <Field k="email" label="Email" type="email" value={form.email} onChange={upd} />
      <Field k="phone" label="Phone" mono value={form.phone} onChange={upd} />
      <Field k="hire_date" label="Hire Date" type="date" value={form.hire_date} onChange={upd} />
      <Field k="address" label="Home Address" full value={form.address} onChange={upd} />
      <Field k="allergies" label="Allergies" full value={form.allergies} onChange={upd} />
      <Field k="emergency_contact_name" label="Emergency Contact Name"
             value={form.emergency_contact_name} onChange={upd} />
      <Field k="emergency_contact_phone" label="Emergency Contact Phone" mono
             value={form.emergency_contact_phone} onChange={upd} />
      <Field k="emergency_contact_relationship" label="Relationship"
             value={form.emergency_contact_relationship} onChange={upd} />
    </div>
  );
}

export function StepPin({ form, upd }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg flex items-start gap-2">
        <KeyRound className="w-4 h-4 text-pink-700 mt-0.5 shrink-0" />
        <p className="text-xs text-pink-900">
          The PIN unlocks the /clockin kiosk. Must be a unique 4–8 digit number. Leave blank to skip and set later.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Clock-in PIN (4-8 digits)</Label>
          <Input data-testid="onboard-pin" type="password" inputMode="numeric" value={form.pin}
            onChange={(e) => upd("pin", e.target.value.replace(/\D/g, ""))}
            className="font-mono tracking-widest text-lg" />
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Confirm PIN</Label>
          <Input data-testid="onboard-pin-confirm" type="password" inputMode="numeric" value={form.pin_confirm}
            onChange={(e) => upd("pin_confirm", e.target.value.replace(/\D/g, ""))}
            className="font-mono tracking-widest text-lg" />
        </div>
        <Field k="hourly_rate" label="Hourly Rate (USD)" type="number" value={form.hourly_rate} onChange={upd} />
        <Field k="initial_pto_hours" label="Annual PTO Hours" type="number"
               value={form.initial_pto_hours} onChange={upd} />
      </div>
    </div>
  );
}

export function StepLicense({ form, upd, today }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg flex items-start gap-2">
        <Award className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
        <p className="text-xs text-sky-900">
          If a license # + expiry are provided, a license record will be auto-created and tracked with renewal alerts.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field k="license_number" label="License #" mono value={form.license_number} onChange={upd} />
        <Field k="license_expiry" label="License Expiration" type="date" minDate={today}
               value={form.license_expiry} onChange={upd} />
        <Field k="issuing_state" label="Issuing State (e.g. OH)" value={form.issuing_state} onChange={upd} />
      </div>
    </div>
  );
}

export function StepPayroll({ form, upd }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-pink-700 mt-0.5 shrink-0" />
        <p className="text-xs text-pink-900">
          All payroll fields are encrypted at rest (AES-256-GCM). Access and edits are audit-logged per HIPAA §164.312.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field k="ssn" label="SSN" mono value={form.ssn} onChange={upd} />
        <Field k="dob" label="Date of Birth" type="date" value={form.dob} onChange={upd} />
        <Field k="bank_account" label="Bank Account #" mono value={form.bank_account} onChange={upd} />
        <Field k="routing_number" label="Routing #" mono value={form.routing_number} onChange={upd} />
      </div>
      <p className="text-xs text-slate-400">
        All fields on this step are optional — you can add them later from the Payroll dialog on the employee row.
      </p>
    </div>
  );
}
